import { useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import type { ExaminationFormValues } from '@/entities/visit/model/examination.schema'
import { useCreateMedicalDeferralMutation } from '@/entities/visit/api'
import { useAuthStore } from '@/shared/store/authStore'
import { getOrganizationIdFromUser } from '@/shared/utils/get-organization-id'
import { toaster } from '@/shared/ui'
import { medicalDeferralSchema } from './medical-deferral.schema'
import type { MedicalDeferralFormValues } from './medical-deferral.schema'
import { apiFieldKeyToSnakeCase, getAxiosErrorData } from '@/shared/utils'
import type { IApiResponse } from '@/shared/types/api'

interface Options {
  visitId: string
  donorId: string
  examinationForm: UseFormReturn<ExaminationFormValues>
  onSuccess?: () => void
}

export function useMedicalDeferralForm({
  visitId,
  donorId,
  examinationForm,
  onSuccess,
}: Options) {
  const { t } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const schema = useMemo(() => medicalDeferralSchema(t), [t])

  const { mutateAsync, isPending } = useCreateMedicalDeferralMutation(
    visitId,
    donorId,
  )

  const form = useForm<MedicalDeferralFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      deferral_end_date: '',
    },
  })

  const onSubmit = form.handleSubmit(async (data) => {
    const isExaminationValid = await examinationForm.trigger()
    if (!isExaminationValid) return

    const examinationData = examinationForm.getValues()

    try {
      await mutateAsync({
        examination: {
          weight_kg: Number(examinationData.weight_kg),
          height_cm: Number(examinationData.height_cm),
          blood_pressure_systolic: Number(
            examinationData.blood_pressure_systolic,
          ),
          blood_pressure_diastolic: Number(
            examinationData.blood_pressure_diastolic,
          ),
          pulse: Number(examinationData.pulse),
          conclusion: examinationData.conclusion,
          needs_ambulatory_exam: examinationData.needs_ambulatory_exam,
          referral_organization_id: examinationData.referral_organization_id,
        },
        deferral_end_date: data.deferral_end_date,
        organization_id: getOrganizationIdFromUser(user),
      })
      toaster.success({
        title: t('donor_detail.medical_history.deferral_modal.success'),
      })
      form.reset()
      onSuccess?.()
    } catch (error) {
      const body = getAxiosErrorData<IApiResponse<null>>(error)
      if (body?.errors[0]?.code === '200000') {
        body.errors[0]?.fields?.forEach((field) => {
          const [prefix, ...rest] = field.key.split('.')
          const message = field.value

          if (prefix === 'examination' && rest.length > 0) {
            const fieldName = apiFieldKeyToSnakeCase(rest.join('.'))
            examinationForm.setError(
              fieldName as keyof ExaminationFormValues & string,
              { message },
            )
            return
          }

          const fieldName = apiFieldKeyToSnakeCase(field.key)
          form.setError(fieldName as keyof MedicalDeferralFormValues & string, {
            message,
          })
        })
        return
      }
      if (body?.errors[0]?.message) {
        body.errors.forEach((error) => {
          toaster.error({ title: error.message })
        })
        return
      }
      toaster.error({
        title: t('donor_detail.medical_history.deferral_modal.error'),
      })
    }
  })

  return { form, isPending, onSubmit }
}
